import { useState, useMemo, useCallback, useEffect, useRef, Fragment } from "react";
import {
  Package,
  Wrench,
  ClipboardList,
  Ruler,
  FileEdit,
  CreditCard,
  Coins,
  FileText,
  ChevronRight,
  PanelRightClose,
  PanelRightOpen,
  User,
  Loader2,
  Plus,
} from "lucide-react";
import { buildClientsHash, buildSoumissionHash, getSoumissionQueryFromHash } from "../../routing";
import { BUNDLE_FAMILY_LABELS, type BundleFamily } from "../../api/catalog";
import { AppShell } from "../layouts/AppShell";
import { Button } from "../primitives/Button";
import { cn } from "../utils/cn";
import {
  createQuote,
  getQuote,
  listQuotes,
  addQuoteLine,
  updateQuoteLine,
  deleteQuoteLine,
  sendQuote,
  dissociateQuoteBundle,
  updateQuote,
  type QuoteOut,
  type QuoteLineOut,
  type QuoteBundleOut,
} from "../../api/quotes";
import { ConfigureBundleModal } from "../components/quote/ConfigureBundleModal";
import { SendQuoteModal } from "../components/quote/SendQuoteModal";
import { getApiBase } from "../../api/auth";
import { getAdminOrgId, getFarm } from "../../api/admin";
import { listProducts, simulatePrice, type Product, type ProductType } from "../../api/catalog";

function getPublicQuoteUrl(token: string): string {
  const base = getApiBase();
  if (base.startsWith("http")) {
    return base.replace(/\/api\/?$/, "") + "/api/public/quotes/" + token;
  }
  return (typeof window !== "undefined" ? window.location.origin : "") + "/api/public/quotes/" + token;
}

function formatCurrency(value: number): string {
  return new Intl.NumberFormat("fr-CA", { style: "currency", currency: "CAD" }).format(value);
}

type SectionId =
  | "bundles_drone_operation"
  | "bundles_drone_cartographie"
  | "bundles_agriculture_precision"
  | "parts"
  | "service_fixed"
  | "service_variable"
  | "service_custom"
  | "subscription"
  | "credits"
  | "summary";

/** 3 types de bundle = 3 sections dans le menu. */
const BUNDLE_FAMILY_SECTIONS: { id: SectionId; label: string; familyKey: BundleFamily }[] = [
  { id: "bundles_drone_operation", label: "Vente de drone", familyKey: "drone_operation" },
  { id: "bundles_drone_cartographie", label: "Services à forfait", familyKey: "drone_cartographie" },
  { id: "bundles_agriculture_precision", label: "Agriculture de précision", familyKey: "agriculture_precision" },
];

/** 3 sections bundle (par type) + autres sections + Résumé. */
const SECTIONS: { id: SectionId; label: string; icon: typeof Package }[] = [
  ...BUNDLE_FAMILY_SECTIONS.map((s) => ({ id: s.id, label: s.label, icon: Package })),
  { id: "parts", label: "Pièces / Accessoires", icon: Wrench },
  { id: "service_fixed", label: "Services fixes", icon: ClipboardList },
  { id: "service_variable", label: "Services par superficie / quantité", icon: Ruler },
  { id: "service_custom", label: "Services personnalisés", icon: FileEdit },
  { id: "subscription", label: "Abonnements / Licences", icon: CreditCard },
  { id: "credits", label: "Crédits XLHub", icon: Coins },
  { id: "summary", label: "Résumé & Paramètres", icon: FileText },
];

/** Sections qui chargent un catalogue (filtre API). Certaines sections acceptent plusieurs types (tableau). */
const SECTION_CATALOG_FILTER: Partial<Record<SectionId, { product_type: ProductType | ProductType[] }>> = {
  bundles_drone_operation: { product_type: "bundle" },
  bundles_drone_cartographie: { product_type: "bundle" },
  bundles_agriculture_precision: { product_type: "bundle" },
  parts: { product_type: ["produit", "consommable"] },
  subscription: { product_type: "licence" },
  service_fixed: { product_type: ["service_fixed", "service"] },
  service_variable: { product_type: "service_area" },
  credits: { product_type: "license_credit" },
};

const BUNDLE_SECTION_IDS = new Set<SectionId>([
  "bundles_drone_operation",
  "bundles_drone_cartographie",
  "bundles_agriculture_precision",
]);

const CATALOG_SECTION_IDS = new Set<SectionId>([
  "bundles_drone_operation",
  "bundles_drone_cartographie",
  "bundles_agriculture_precision",
  "parts",
  "subscription",
  "service_fixed",
  "service_variable",
  "credits",
]);

type QuoteBuilderPageProps = {
  userName?: string;
  /** Client pré-sélectionné (ex. depuis la liste clients). */
  preselectedClientId?: string | null;
  preselectedClientName?: string | null;
  /** Soumission à rouvrir (ex. depuis la liste ou lien). */
  preselectedQuoteId?: string | null;
};

export function QuoteBuilderPage({
  userName = "Utilisateur",
  preselectedClientId,
  preselectedClientName,
  preselectedQuoteId,
}: QuoteBuilderPageProps) {
  const [activeSection, setActiveSection] = useState<SectionId>("bundles_drone_operation");
  const [clientId, setClientId] = useState<string | null>(preselectedClientId ?? null);
  const [clientName, setClientName] = useState<string | null>(preselectedClientName ?? null);
  const [quoteId, setQuoteId] = useState<string | null>(preselectedQuoteId ?? null);
  const [quote, setQuote] = useState<QuoteOut | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [quotesList, setQuotesList] = useState<QuoteOut[]>([]);
  const [quotesListLoading, setQuotesListLoading] = useState(false);
  const [catalogProducts, setCatalogProducts] = useState<Product[]>([]);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [addingProductId, setAddingProductId] = useState<string | null>(null);
  const [quantityByProductId, setQuantityByProductId] = useState<Record<string, number>>({});
  const [lineUpdatingId, setLineUpdatingId] = useState<string | null>(null);
  const [lineRemovingId, setLineRemovingId] = useState<string | null>(null);
  const [lineQuantityDraft, setLineQuantityDraft] = useState<Record<string, number>>({});
  const [summaryPanelCollapsed, setSummaryPanelCollapsed] = useState(false);
  const [bundleModalProduct, setBundleModalProduct] = useState<Product | null>(null);
  /** When set, modal opens in edit mode for this quote bundle (same product as bundleModalProduct). */
  const [editingBundleId, setEditingBundleId] = useState<string | null>(null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [primaryContactEmail, setPrimaryContactEmail] = useState<string | null>(null);
  const [echeance, setEcheance] = useState<string>("");
  const [echeanceDays, setEcheanceDays] = useState<string>("");
  const [echeanceCustomDate, setEcheanceCustomDate] = useState<string>("");
  const [modalites, setModalites] = useState<string>("");
  const [depositFixedType, setDepositFixedType] = useState<"amount" | "percent">("amount");
  const [depositFixedValue, setDepositFixedValue] = useState<string>("");
  const [conditionsPaiement, setConditionsPaiement] = useState<string>("");
  const [pdfShowCredits, setPdfShowCredits] = useState(true);
  const [pdfShowServiceOffer, setPdfShowServiceOffer] = useState(true);
  const autoCreateAttemptedRef = useRef(false);

  const loadQuote = useCallback(async (id: string) => {
    setLoading(true);
    setError(null);
    try {
      const data = await getQuote(id);
      setQuote(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur chargement soumission");
    } finally {
      setLoading(false);
    }
  }, []);

  const handleCreateQuote = useCallback(async () => {
    if (!clientId) return;
    setLoading(true);
    setError(null);
    try {
      const orgId = await getAdminOrgId();
      const data = await createQuote({
        org_id: orgId,
        client_id: clientId,
        currency: "CAD",
        lines: [],
      });
      setQuoteId(data.id);
      setQuote(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur création soumission");
    } finally {
      setLoading(false);
    }
  }, [clientId]);

  const handleSend = useCallback(async () => {
    if (!quoteId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await sendQuote(quoteId);
      setQuote(data);
    } catch (e) {
      setError(e instanceof Error ? e.message : "Erreur envoi");
    } finally {
      setLoading(false);
    }
  }, [quoteId]);

  const handleAddProductToQuote = useCallback(
    async (product: Product, quantity: number) => {
      if (!quoteId || quantity < 0.001) return;
      const qty = Math.max(1, Math.round(quantity));
      setAddingProductId(product.id);
      setError(null);
      try {
        const sim = await simulatePrice({
          product_id: product.id,
          quantity: qty,
          currency: product.default_currency || "CAD",
          tax_profile: product.tax_profile,
        });
        await addQuoteLine(quoteId, {
          product_id: product.id,
          label: product.name,
          product_type: product.product_type,
          ops_family: product.ops_family ?? undefined,
          quantity: qty,
          unit_kind: product.unit_kind ?? undefined,
          unit_price: sim.unit_price,
          tax_code: product.tax_code ?? undefined,
        });
        const data = await getQuote(quoteId);
        setQuote(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Échec de l'ajout du produit");
      } finally {
        setAddingProductId(null);
      }
    },
    [quoteId]
  );

  const handleUpdateQuoteLineQuantity = useCallback(
    async (lineId: string, quantity: number) => {
      if (!quoteId) return;
      const qty = Math.max(1, Math.round(quantity));
      setLineUpdatingId(lineId);
      setError(null);
      try {
        await updateQuoteLine(quoteId, lineId, { quantity: qty });
        const data = await getQuote(quoteId);
        setQuote(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Échec de la mise à jour");
      } finally {
        setLineUpdatingId(null);
      }
    },
    [quoteId]
  );

  const handleRemoveProductFromQuote = useCallback(
    async (lineId: string) => {
      if (!quoteId) return;
      setLineRemovingId(lineId);
      setError(null);
      try {
        await deleteQuoteLine(quoteId, lineId);
        const data = await getQuote(quoteId);
        setQuote(data);
      } catch (e) {
        setError(e instanceof Error ? e.message : "Échec de la suppression");
      } finally {
        setLineRemovingId(null);
      }
    },
    [quoteId]
  );

  // Sync quoteId from URL (preselectedQuoteId) when it changes (e.g. back to list then open another)
  useEffect(() => {
    if (preselectedQuoteId !== undefined && preselectedQuoteId !== quoteId) {
      setQuoteId(preselectedQuoteId ?? null);
      if (!preselectedQuoteId) setQuote(null);
    }
  }, [preselectedQuoteId]);

  useEffect(() => {
    if (quoteId && !quote) loadQuote(quoteId);
  }, [quoteId, quote, loadQuote]);

  // When quote is loaded, sync client from quote (e.g. opened by quoteId from list)
  useEffect(() => {
    if (!quote) return;
    if (quote.client_id !== clientId) setClientId(quote.client_id);
    if (!clientName && quote.client_id) {
      getFarm(quote.client_id).then((f) => setClientName(f.organization?.name ?? null)).catch(() => {});
    }
  }, [quote?.id, quote?.client_id]);

  // Sync URL when we have a quote open so that refresh keeps the same quote
  useEffect(() => {
    if (!quoteId || !quote) return;
    const { quoteId: currentQuoteId } = getSoumissionQueryFromHash();
    if (currentQuoteId === quoteId) return;
    const nextHash = buildSoumissionHash({ quoteId, clientId: quote.client_id, clientName: clientName ?? undefined });
    window.location.replace(nextHash);
  }, [quoteId, quote, clientName]);

  // Liste des soumissions : charger quand on n'a pas de quote à ouvrir
  useEffect(() => {
    if (quoteId || preselectedClientId) return;
    setQuotesListLoading(true);
    listQuotes({ page_size: 100 })
      .then((res) => setQuotesList(res.items ?? []))
      .catch(() => setQuotesList([]))
      .finally(() => setQuotesListLoading(false));
  }, [quoteId, preselectedClientId]);

  // Client déjà sélectionné (ex. depuis la liste) → créer le brouillon automatiquement, sans clic "Créer la soumission"
  useEffect(() => {
    if (!preselectedClientId || quoteId || autoCreateAttemptedRef.current) return;
    autoCreateAttemptedRef.current = true;
    setLoading(true);
    setError(null);
    getAdminOrgId()
      .then((orgId) =>
        createQuote({
          org_id: orgId,
          client_id: preselectedClientId,
          currency: "CAD",
          lines: [],
        })
      )
      .then((data) => {
        setQuoteId(data.id);
        setQuote(data);
        setClientId(preselectedClientId);
        setClientName(preselectedClientName ?? null);
      })
      .catch((e) => {
        setError(e instanceof Error ? e.message : "Erreur création soumission");
      })
      .finally(() => {
        setLoading(false);
      });
  }, [preselectedClientId, preselectedClientName, quoteId]);

  useEffect(() => {
    const filter = activeSection ? SECTION_CATALOG_FILTER[activeSection] : null;
    if (!filter) {
      setCatalogProducts([]);
      return;
    }
    const types = Array.isArray(filter.product_type) ? filter.product_type : [filter.product_type];
    let cancelled = false;
    setCatalogLoading(true);
    Promise.all(
      types.map((product_type) =>
        listProducts({ product_type, status: "active", page_size: 500 }).then((res) => res.items ?? [])
      )
    )
      .then((results) => {
        if (cancelled) return;
        const byId = new Map<string, Product>();
        for (const items of results) {
          for (const p of items) {
            if (!byId.has(p.id)) byId.set(p.id, p);
          }
        }
        let filtered = Array.from(byId.values());
        if (types.includes("bundle") || (types.length === 1 && types[0] === "bundle")) {
          filtered = filtered.filter((p) => p.product_type === "bundle");
        }
        filtered = filtered.filter((p) => p.product_usage !== "internal_consumption");
        setCatalogProducts(filtered);
      })
      .catch(() => {
        if (!cancelled) setCatalogProducts([]);
      })
      .finally(() => {
        if (!cancelled) setCatalogLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [activeSection]);

  // En section Résumé & Paramètres, fermer le panneau droit (résumé déjà dans le contenu principal). Rouvrir pour les autres sections.
  useEffect(() => {
    setSummaryPanelCollapsed(activeSection === "summary");
  }, [activeSection]);

  const productIdsInQuote = useMemo(
    () => new Set((quote?.lines ?? []).map((l) => l.product_id).filter(Boolean) as string[]),
    [quote?.lines]
  );

  /** Bundles groupés par famille (pour les 3 sections bundle). Filtre côté client par type + famille. */
  const bundlesGroupedByFamily = useMemo(() => {
    if (!BUNDLE_SECTION_IDS.has(activeSection) || catalogProducts.length === 0) return [];
    const order: (BundleFamily | "")[] = ["drone_operation", "drone_cartographie", "agriculture_precision", ""];
    const byFamily: Record<string, Product[]> = {};
    for (const p of catalogProducts) {
      if (p.product_type !== "bundle") continue;
      const key = (p.bundle_family && (p.bundle_family as BundleFamily)) || "";
      if (!byFamily[key]) byFamily[key] = [];
      byFamily[key].push(p);
    }
    return order
      .filter((key) => byFamily[key]?.length)
      .map((key) => ({
        familyKey: key,
        label: key ? BUNDLE_FAMILY_LABELS[key as BundleFamily] : "Sans famille",
        products: byFamily[key] ?? [],
      }));
  }, [activeSection, catalogProducts]);

  /** Famille correspondant à la section bundle active (pour n'afficher que ce type). */
  const activeBundleFamilyKey = useMemo((): BundleFamily | "" => {
    const section = BUNDLE_FAMILY_SECTIONS.find((s) => s.id === activeSection);
    return section ? section.familyKey : "";
  }, [activeSection]);

  /** Compteurs par section pour la nav. Pour les 3 sections bundle : nombre de bundles ajoutés de cette famille. */
  const sectionCounts = useMemo((): Partial<Record<SectionId, number>> => {
    if (!quote) return {};
    const lines = quote.lines ?? [];
    const bundles = quote.bundles ?? [];
    const productIdToFamily = new Map<string, string>();
    for (const p of catalogProducts) {
      if (p.product_type === "bundle" && p.bundle_family) productIdToFamily.set(p.id, p.bundle_family);
    }
    const counts: Partial<Record<SectionId, number>> = {
      parts: lines.filter((l) => (l.product_type === "produit" || l.product_type === "consommable") && !l.quote_bundle_id).length,
      service_fixed: lines.filter((l) => (l.product_type === "service_fixed" || l.product_type === "service") && !l.quote_bundle_id).length,
      service_variable: lines.filter((l) => l.product_type === "service_area" && !l.quote_bundle_id).length,
      subscription: lines.filter((l) => l.product_type === "licence" && !l.quote_bundle_id).length,
      credits: lines.filter((l) => l.product_type === "license_credit" && !l.quote_bundle_id).length,
    };
    for (const s of BUNDLE_FAMILY_SECTIONS) {
      counts[s.id] = bundles.filter((b) => productIdToFamily.get(b.bundle_product_id) === s.familyKey).length;
    }
    return counts;
  }, [quote, catalogProducts]);

  useEffect(() => {
    if (quote?.billing_schedule && typeof quote.billing_schedule === "object") {
      const bs = quote.billing_schedule as Record<string, unknown>;
      if (bs.echeance != null) setEcheance(String(bs.echeance));
      if (bs.echeance_days != null) setEcheanceDays(String(bs.echeance_days));
      if (bs.echeance_custom_date != null) setEcheanceCustomDate(String(bs.echeance_custom_date));
      if (bs.modalites != null) setModalites(String(bs.modalites));
      if (bs.deposit_fixed_type === "percent" || bs.deposit_fixed_type === "amount") setDepositFixedType(bs.deposit_fixed_type as "amount" | "percent");
      if (bs.deposit_fixed_value != null) setDepositFixedValue(String(bs.deposit_fixed_value));
      if (bs.conditions_paiement != null) setConditionsPaiement(String(bs.conditions_paiement));
      if (typeof bs.pdf_show_credits === "boolean") setPdfShowCredits(bs.pdf_show_credits);
      if (typeof bs.pdf_show_service_offer === "boolean") setPdfShowServiceOffer(bs.pdf_show_service_offer);
    }
    if (quote?.valid_until && echeance === "") {
      const d = quote.valid_until.slice(0, 10);
      setEcheanceCustomDate(d);
    }
  }, [quote?.id]);

  useEffect(() => {
    if (quote?.valid_until) setEcheanceCustomDate(quote.valid_until.slice(0, 10));
  }, [quote?.valid_until]);

  useEffect(() => {
    if (quote?.client_id && sendModalOpen) {
      getFarm(quote.client_id)
        .then((farm) => setPrimaryContactEmail(farm.primary_contact?.person?.email ?? null))
        .catch(() => setPrimaryContactEmail(null));
    }
  }, [quote?.client_id, sendModalOpen]);

  const saveBillingParams = useCallback(
    async (updates: Record<string, unknown>) => {
      if (!quoteId || !quote) return;
      const bs = { ...(quote.billing_schedule as Record<string, unknown>), ...updates };
      try {
        const data = await updateQuote(quoteId, { billing_schedule: bs });
        setQuote(data);
      } catch (_e) {
        setError("Erreur lors de l'enregistrement des paramètres");
      }
    },
    [quoteId, quote]
  );

  const sidebar = (
    <nav className="flex flex-col gap-0.5 py-1 w-full max-w-[220px]" aria-label="Sections soumission">
      {SECTIONS.filter((s) => s.id !== "summary").map((section) => {
        const Icon = section.icon;
        const isActive = activeSection === section.id;
        const count = sectionCounts[section.id];
        const showCount = count !== undefined && count > 0;
        return (
          <button
            key={section.id}
            type="button"
            onClick={() => setActiveSection(section.id)}
            className={cn(
              "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition border-l-2 -ml-px pl-2.5 min-w-0",
              isActive ? "bg-primary/10 text-primary font-medium border-primary" : "border-transparent text-muted hover:bg-surface-muted/70 hover:text-text"
            )}
          >
            <Icon size={16} className="shrink-0" aria-hidden />
            <span className="truncate">{section.label}</span>
            {showCount && (
              <span className="ml-auto shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-xs tabular-nums">
                {count}
              </span>
            )}
          </button>
        );
      })}
      <button
        type="button"
        onClick={() => setActiveSection("summary")}
        className={cn(
          "flex w-full items-center gap-2 rounded-md px-2.5 py-2 text-left text-sm transition border-l-2 -ml-px pl-2.5 min-w-0",
          activeSection === "summary" ? "bg-primary/10 text-primary font-medium border-primary" : "border-transparent text-muted hover:bg-surface-muted/70 hover:text-text"
        )}
      >
        <FileText size={16} className="shrink-0" aria-hidden />
        <span className="truncate">Résumé & Paramètres</span>
      </button>
    </nav>
  );

  return (
    <AppShell
      userName={userName}
      sidebar={sidebar}
      sidebarTitle="Soumission"
      sidebarPinnedWidth={200}
      containerFluid={true}
      modules={[
        { label: "Tableau de bord", onClick: () => (window.location.hash = "#/") },
        {
          label: "Clients",
          onClick: () => (window.location.hash = clientId ? buildClientsHash(clientId) : "#/clients"),
          separator: true,
        },
        { label: "Soumission (devis)", onClick: () => (window.location.hash = "#/soumission"), sectionHeader: true },
      ]}
    >
      <div className="flex flex-1 min-h-0 w-full min-w-0 p-6 gap-0 overflow-hidden">
        {/* Fenêtre de travail (centre) */}
        <main className="min-w-0 flex-1 flex flex-col overflow-auto rounded-lg border border-border bg-surface shadow-sm min-h-0">
          {/* Header : Retour liste soumissions, Client, Soumission, Statut, actions */}
          <div className="shrink-0 flex flex-wrap items-center gap-3 rounded-t-lg border-b border-border bg-muted/30 px-4 py-3">
            {(clientId || quoteId) ? (
              <Button
                label="Retour à la liste des soumissions"
                intent="ghost"
                size="sm"
                onClick={() => (window.location.hash = buildSoumissionHash())}
                className="mr-1 -ml-1"
              />
            ) : null}
            {clientId ? <span className="text-muted">|</span> : null}
            <User size={20} className="text-muted" />
            <span className="text-sm text-muted">Client :</span>
            {clientId && clientName ? (
              <span className="font-medium text-text">{clientName}</span>
            ) : (
              <span className="text-sm text-muted">Non sélectionné</span>
            )}
            <Button
              label={clientId ? "Changer" : "Sélectionner un client"}
              intent="secondary"
              size="sm"
              onClick={() => (window.location.hash = "#/clients")}
            />
            {clientId && !quoteId && (
              <>
                {loading && preselectedClientId ? (
                  <span className="flex items-center gap-2 text-sm text-muted">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Démarrage de la soumission…
                  </span>
                ) : (
                  <Button
                    label={loading ? "Création…" : "Créer la soumission"}
                    intent="primary"
                    size="sm"
                    onClick={handleCreateQuote}
                    disabled={loading}
                  />
                )}
              </>
            )}
            {quoteId && quote && (
              <>
                <span className="text-xs text-muted">Soumission créée</span>
                <span className="text-xs text-muted">·</span>
                <span className="text-sm font-medium text-text">{quote.quote_number}</span>
                <span className="text-xs text-muted">({quote.status === "draft" ? "Brouillon" : quote.status})</span>
                {quote.status === "draft" && (
                  <div className="ml-auto flex gap-2">
                    <Button
                      label={loading ? "Envoi…" : "Envoyer"}
                      intent="primary"
                      size="sm"
                      onClick={handleSend}
                      disabled={loading}
                    />
                  </div>
                )}
              </>
            )}
          </div>
          {error && (
            <div className="mx-4 mb-4 rounded-md border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">
              {error}
            </div>
          )}

          {!quoteId && !preselectedClientId ? (
            <div className="flex-1 overflow-y-auto p-6">
              <h2 className="mb-2 text-lg font-semibold text-text">Liste des soumissions</h2>
              <p className="text-sm text-muted mb-4">Ouvrez une soumission pour la modifier ou créez-en une nouvelle.</p>
              <div className="flex flex-wrap gap-3 mb-6">
                <Button
                  label="Nouvelle soumission"
                  intent="primary"
                  size="sm"
                  onClick={() => (window.location.hash = "#/clients")}
                />
              </div>
              {quotesListLoading ? (
                <p className="flex items-center gap-2 text-sm text-muted">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Chargement…
                </p>
              ) : quotesList.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border bg-muted/20 p-8 text-center">
                  <p className="text-sm text-muted">Aucune soumission. Créez-en une depuis la liste des clients.</p>
                  <Button
                    label="Voir les clients"
                    intent="secondary"
                    size="sm"
                    className="mt-3"
                    onClick={() => (window.location.hash = "#/clients")}
                  />
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden">
                  <table className="w-full text-sm border-collapse">
                    <thead>
                      <tr className="text-left text-muted border-b border-border bg-muted/20">
                        <th className="py-2 px-4 font-medium">N°</th>
                        <th className="py-2 px-4 font-medium">Statut</th>
                        <th className="py-2 px-4 font-medium">Total</th>
                        <th className="py-2 px-4 font-medium">Mise à jour</th>
                        <th className="py-2 px-4 w-24" />
                      </tr>
                    </thead>
                    <tbody>
                      {quotesList.map((q) => (
                        <tr key={q.id} className="border-b border-border/50 hover:bg-muted/10">
                          <td className="py-2.5 px-4 font-medium text-text">{q.quote_number}</td>
                          <td className="py-2.5 px-4">
                            <span className={q.status === "draft" ? "text-muted" : ""}>
                              {q.status === "draft" ? "Brouillon" : q.status === "sent" ? "Envoyée" : q.status}
                            </span>
                          </td>
                          <td className="py-2.5 px-4 tabular-nums">{formatCurrency(q.total)}</td>
                          <td className="py-2.5 px-4 text-muted">
                            {q.updated_at ? new Date(q.updated_at).toLocaleDateString("fr-CA", { dateStyle: "short" }) : "—"}
                          </td>
                          <td className="py-2.5 px-4">
                            <Button
                              label="Ouvrir"
                              intent="secondary"
                              size="sm"
                              onClick={() => (window.location.hash = buildSoumissionHash({ quoteId: q.id }))}
                            />
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          ) : (
          <div className="flex-1 overflow-y-auto p-6">
          {/* Titre de la section active */}
          <h2 className="mb-4 text-lg font-semibold text-text">
            {SECTIONS.find((s) => s.id === activeSection)?.label ?? activeSection}
          </h2>

          {/* Résumé & Paramètres */}
          {activeSection === "summary" && (
            <div className="space-y-8">
              {!quoteId || !quote ? (
                <div className="rounded-md border border-dashed border-border bg-muted/20 p-8 text-center">
                  <p className="text-sm text-muted">Créez une soumission pour configurer les paramètres.</p>
                </div>
              ) : (
                <>
                  {/* 1. Résumé complet */}
                  <div className="rounded-lg border border-border bg-surface overflow-hidden">
                    <h3 className="text-sm font-semibold text-text px-4 py-3 border-b border-border bg-muted/10">Résumé de la soumission</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm border-collapse">
                        <thead>
                          <tr className="text-left text-muted border-b border-border bg-muted/20">
                            <th className="py-2 pr-3 font-medium pl-4">Désignation</th>
                            <th className="py-2 pr-3 font-medium text-right w-20">Qté</th>
                            <th className="py-2 pr-3 font-medium text-right w-28">Prix unit.</th>
                            <th className="py-2 font-medium text-right w-28 pr-4">Total</th>
                          </tr>
                        </thead>
                        <tbody className="text-text">
                          {quote.bundles?.length
                            ? [...quote.bundles]
                                .sort((a, b) => a.position - b.position)
                                .map((bundle) => {
                                  const bundleLines = (quote.lines ?? []).filter((l) => l.quote_bundle_id === bundle.id).sort((a, b) => a.line_order - b.line_order);
                                  return (
                                    <Fragment key={bundle.id}>
                                      <tr className="border-b border-border/50 bg-muted/5">
                                        <td colSpan={4} className="py-1.5 pl-4 font-medium text-text">{bundle.name}</td>
                                      </tr>
                                      {bundleLines.map((line) => (
                                        <tr key={line.id} className="border-b border-border/30">
                                          <td className="py-1.5 pl-6 pr-3">{line.label}</td>
                                          <td className="py-1.5 pr-3 text-right tabular-nums">{line.quantity} {line.unit_kind ?? ""}</td>
                                          <td className="py-1.5 pr-3 text-right tabular-nums">{formatCurrency(line.unit_price)}</td>
                                          <td className="py-1.5 pr-4 text-right tabular-nums">{formatCurrency(line.total_price)}</td>
                                        </tr>
                                      ))}
                                    </Fragment>
                                  );
                                })
                            : null}
                          {(quote.lines ?? []).filter((l) => !l.quote_bundle_id).sort((a, b) => a.line_order - b.line_order).map((line) => (
                            <tr key={line.id} className="border-b border-border/50">
                              <td className="py-1.5 pl-4 pr-3">{line.label}</td>
                              <td className="py-1.5 pr-3 text-right tabular-nums">{line.quantity} {line.unit_kind ?? ""}</td>
                              <td className="py-1.5 pr-3 text-right tabular-nums">{formatCurrency(line.unit_price)}</td>
                              <td className="py-1.5 pr-4 text-right tabular-nums">{formatCurrency(line.total_price)}</td>
                            </tr>
                          ))}
                        </tbody>
                        <tfoot className="bg-muted/10">
                          <tr className="border-t-2 border-border">
                            <td colSpan={3} className="py-2 pl-4 font-medium text-text">Sous-total</td>
                            <td className="py-2 pr-4 text-right tabular-nums font-medium">{formatCurrency(quote.subtotal)}</td>
                          </tr>
                          <tr className="border-t border-border">
                            <td colSpan={3} className="py-2 pl-4 text-muted">Taxes</td>
                            <td className="py-2 pr-4 text-right tabular-nums text-muted">{formatCurrency(quote.tax_total ?? 0)}</td>
                          </tr>
                          <tr className="border-t-2 border-border font-semibold text-text">
                            <td colSpan={3} className="py-2 pl-4">Total</td>
                            <td className="py-2 pr-4 text-right tabular-nums">{formatCurrency(quote.total)}</td>
                          </tr>
                        </tfoot>
                      </table>
                    </div>
                  </div>

                  {/* 2. Paramètres */}
                  <div className="rounded-lg border border-border bg-muted/5 p-4">
                    <h3 className="text-sm font-semibold text-text mb-2">Paramétrage de la soumission</h3>
                    <p className="text-xs text-muted mb-4">Par défaut, rien n&apos;est sélectionné. Veuillez choisir une option dans chaque liste.</p>
                    <div className="grid gap-4 sm:grid-cols-3">
                      <div>
                        <label className="block text-sm font-medium text-text mb-1">Échéance</label>
                        <select
                          value={echeance}
                          onChange={(e) => {
                            const v = e.target.value;
                            setEcheance(v);
                            if (v) saveBillingParams({ echeance: v, echeance_days: v === "x_days" ? echeanceDays : undefined });
                          }}
                          className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                        >
                          <option value="">Sélectionner...</option>
                          <option value="30_days">Valide 30 jours (à partir du 1er envoi)</option>
                          <option value="60_days">Valide 60 jours (à partir du 1er envoi)</option>
                          <option value="x_days">Valide X jours (à partir du 1er envoi)</option>
                          <option value="custom_date">Date d&apos;échéance au choix</option>
                        </select>
                        {echeance === "x_days" && (
                          <div className="mt-2">
                            <label className="block text-xs font-medium text-muted mb-1">Nombre de jours</label>
                            <input
                              type="number"
                              min={1}
                              placeholder="ex. 45"
                              value={echeanceDays}
                              onChange={(e) => setEcheanceDays(e.target.value)}
                              onBlur={() => echeance === "x_days" && saveBillingParams({ echeance_days: echeanceDays || undefined })}
                              className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm"
                            />
                            <p className="text-xs text-muted mt-1">L&apos;échéance sera calculée à partir de la date du premier envoi de la soumission.</p>
                          </div>
                        )}
                        {echeance === "custom_date" && (
                          <div className="mt-2">
                            <label className="block text-xs font-medium text-muted mb-1">Date d&apos;échéance</label>
                            <input
                              type="date"
                              value={echeanceCustomDate}
                              onChange={(e) => {
                                const v = e.target.value;
                                setEcheanceCustomDate(v);
                                if (v) saveBillingParams({ echeance_custom_date: v });
                                if (quoteId && v) updateQuote(quoteId, { valid_until: v }).then((data) => setQuote(data));
                              }}
                              className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm"
                            />
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text mb-1">Modalités d&apos;acompte</label>
                        <select
                          value={modalites}
                          onChange={(e) => {
                            const v = e.target.value;
                            setModalites(v);
                            if (v) saveBillingParams({ modalites: v });
                          }}
                          className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                        >
                          <option value="">Sélectionner...</option>
                          <option value="none">Aucun acompte – 100 % à la livraison</option>
                          <option value="30_percent">Acompte de 30 % à l&apos;acceptation – Solde à la livraison</option>
                          <option value="50_percent">Acompte de 50 % à l&apos;acceptation – Solde à la livraison</option>
                          <option value="fixed">Acompte montant fixe à l&apos;acceptation – Solde à la livraison</option>
                          <option value="full">Paiement complet à l&apos;acceptation (100 %)</option>
                        </select>
                        {modalites === "fixed" && (
                          <div className="mt-2 space-y-2">
                            <div className="flex gap-2 items-center">
                              <button
                                type="button"
                                onClick={() => setDepositFixedType("amount")}
                                className={cn("px-2 py-1 rounded text-xs font-medium", depositFixedType === "amount" ? "bg-primary text-white" : "bg-muted/50 text-muted")}
                              >
                                $
                              </button>
                              <button
                                type="button"
                                onClick={() => setDepositFixedType("percent")}
                                className={cn("px-2 py-1 rounded text-xs font-medium", depositFixedType === "percent" ? "bg-primary text-white" : "bg-muted/50 text-muted")}
                              >
                                %
                              </button>
                            </div>
                            <input
                              type={depositFixedType === "percent" ? "number" : "text"}
                              min={depositFixedType === "percent" ? 0 : undefined}
                              max={depositFixedType === "percent" ? 100 : undefined}
                              placeholder={depositFixedType === "amount" ? "Montant en $" : "Pourcentage"}
                              value={depositFixedValue}
                              onChange={(e) => setDepositFixedValue(e.target.value)}
                              onBlur={() => saveBillingParams({ deposit_fixed_type: depositFixedType, deposit_fixed_value: depositFixedValue ? (depositFixedType === "percent" ? Number(depositFixedValue) : parseFloat(depositFixedValue)) : undefined })}
                              className="w-full h-9 rounded-md border border-border bg-surface px-2 text-sm"
                            />
                            <p className="text-xs text-muted">Ce montant sera facturé comme acompte + taxes. Le solde sera facturé à la livraison.</p>
                          </div>
                        )}
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-text mb-1">Conditions de paiement</label>
                        <select
                          value={conditionsPaiement}
                          onChange={(e) => {
                            const v = e.target.value;
                            setConditionsPaiement(v);
                            if (v) saveBillingParams({ conditions_paiement: v });
                          }}
                          className="w-full h-10 rounded-md border border-border bg-surface px-3 text-sm text-text"
                        >
                          <option value="">Sélectionner...</option>
                          <option value="on_receipt">Sur réception</option>
                          <option value="net_15">Net 15 jours</option>
                          <option value="net_30">Net 30 jours</option>
                        </select>
                      </div>
                    </div>
                  </div>

                  {/* 3. Options d'affichage PDF / version en ligne */}
                  <div className="rounded-lg border border-border bg-muted/5 p-4">
                    <h3 className="text-sm font-semibold text-text mb-3">Options d&apos;affichage PDF / version en ligne</h3>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-text mb-2">
                      <input type="checkbox" checked={pdfShowCredits} onChange={(e) => { setPdfShowCredits(e.target.checked); saveBillingParams({ pdf_show_credits: e.target.checked }); }} className="rounded border-border" />
                      Afficher les crédits
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer text-sm text-text">
                      <input type="checkbox" checked={pdfShowServiceOffer} onChange={(e) => { setPdfShowServiceOffer(e.target.checked); saveBillingParams({ pdf_show_service_offer: e.target.checked }); }} className="rounded border-border" />
                      Afficher joindre l&apos;offre de service (détails des descriptions et des livrables)
                    </label>
                  </div>

                  {/* 4. Boutons d'action */}
                  <div className="flex flex-wrap gap-3 pt-4 border-t border-border">
                    <Button label="Visualiser en ligne" intent="default" size="sm" onClick={() => quote?.public_token && window.open(getPublicQuoteUrl(quote.public_token), "_blank")} disabled={!quote?.public_token} />
                    <Button label="Exporter PDF" intent="default" size="sm" onClick={() => { /* TODO: export PDF */ }} />
                    <Button label="Envoyer" intent="primary" size="sm" onClick={() => setSendModalOpen(true)} />
                    <Button label="Corriger" intent="ghost" size="sm" onClick={() => { /* TODO: when declined, allow edit + confirm */ }} title="Lorsque la soumission a été refusée, modifier et confirmer les modifications." />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Sections catalogue (Pièces, Abonnements/Licences, Services, Crédits) : liste + ajout à la soumission */}
          {CATALOG_SECTION_IDS.has(activeSection) && (
            <div className="space-y-4">
              {!quoteId ? (
                <div className="rounded-md border border-dashed border-border bg-muted/20 p-6 text-center">
                  {preselectedClientId && loading ? (
                    <p className="flex items-center justify-center gap-2 text-sm text-muted">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      Démarrage de la soumission…
                    </p>
                  ) : (
                    <p className="text-sm text-muted">
                      Sélectionnez un client pour démarrer, ou créez la soumission pour ajouter des produits.
                    </p>
                  )}
                </div>
              ) : catalogLoading ? (
                <div className="flex items-center justify-center gap-2 py-8 text-muted">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">Chargement du catalogue…</span>
                </div>
              ) : catalogProducts.length === 0 ? (
                <div className="rounded-md border border-dashed border-border bg-muted/20 p-6 text-center">
                  <p className="text-sm text-muted">Aucun produit dans cette catégorie.</p>
                </div>
              ) : BUNDLE_SECTION_IDS.has(activeSection) ? (
                <div className="space-y-8">
                  {(() => {
                    const currentFamilyGroup = bundlesGroupedByFamily.find((g) => g.familyKey === activeBundleFamilyKey);
                    const label = activeBundleFamilyKey ? BUNDLE_FAMILY_LABELS[activeBundleFamilyKey] : "Sans famille";
                    if (!currentFamilyGroup && !catalogLoading) {
                      return (
                        <div className="rounded-md border border-dashed border-border bg-muted/20 p-6 text-center">
                          <p className="text-sm text-muted">
                            Aucun bundle de type « {label} ». Ajoutez des bundles avec cette famille dans le catalogue Produits &amp; services.
                          </p>
                        </div>
                      );
                    }
                    if (!currentFamilyGroup) return null;
                    const { products } = currentFamilyGroup;
                    const productIdsInFamily = new Set(products.map((p) => p.id));
                    const addedBundlesInFamily = (quote?.bundles ?? []).filter((b) =>
                      productIdsInFamily.has(b.bundle_product_id)
                    );
                    return (
                      <>
                        <div>
                          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3 mb-4">
                            {products.map((product) => {
                              const countForProduct = addedBundlesInFamily.filter(
                                (qb) => qb.bundle_product_id === product.id
                              ).length;
                              return (
                                <button
                                  key={product.id}
                                  type="button"
                                  onClick={() => {
                                    setEditingBundleId(null);
                                    setBundleModalProduct(product);
                                  }}
                                  className="flex flex-col text-left rounded-lg border border-border bg-surface p-4 shadow-sm hover:border-primary/50 hover:shadow-md transition-all relative min-h-[120px]"
                                >
                                  <div className="font-medium text-text">{product.name}</div>
                                  {product.code && (
                                    <div className="mt-0.5 text-xs text-muted">{product.code}</div>
                                  )}
                                  {product.description_md && (
                                    <p className="mt-1 line-clamp-2 text-sm text-muted flex-1">
                                      {product.description_md.replace(/#+/g, "").trim().slice(0, 100)}
                                      {product.description_md.length > 100 ? "…" : ""}
                                    </p>
                                  )}
                                  <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                                    {countForProduct > 0 ? (
                                      <span className="text-xs text-muted">
                                        {countForProduct} configuration{countForProduct > 1 ? "s" : ""} dans la soumission
                                      </span>
                                    ) : (
                                      <span />
                                    )}
                                    <span
                                      className="inline-flex h-8 w-8 shrink-0 items-center justify-center rounded-md border border-primary/40 bg-primary/5 text-primary hover:bg-primary/10 transition-colors"
                                      aria-hidden
                                    >
                                      <Plus className="h-4 w-4 stroke-[2.5]" />
                                    </span>
                                  </div>
                                </button>
                              );
                            })}
                          </div>
                          {addedBundlesInFamily.length > 0 && (
                            <div className="rounded-lg border border-border bg-muted/10 p-3">
                              <h4 className="text-xs font-semibold text-muted mb-2">
                                Bundles ajoutés à la soumission
                              </h4>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                  <thead>
                                    <tr className="text-left text-muted border-b border-border">
                                      <th className="py-2 pr-2 font-medium">Type de bundle</th>
                                      <th className="py-2 pr-2 font-medium">Titre</th>
                                      <th className="py-2 pr-2 text-right font-medium w-28">Prix</th>
                                      <th className="py-2 pl-2 w-24" />
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {addedBundlesInFamily.map((qb) => {
                                      const lines = (quote?.lines ?? []).filter((l) => l.quote_bundle_id === qb.id);
                                      const total = lines.reduce((s, l) => s + l.total_price, 0);
                                      const bundleProduct = products.find((p) => p.id === qb.bundle_product_id);
                                      return (
                                        <tr key={qb.id} className="border-b border-border/50 last:border-0">
                                          <td className="py-2 pr-2 text-text">
                                            {bundleProduct?.name ?? "—"}
                                          </td>
                                          <td className="py-2 pr-2 text-text font-medium">{qb.name}</td>
                                          <td className="py-2 pr-2 text-right tabular-nums whitespace-nowrap">
                                            {formatCurrency(total)}
                                          </td>
                                          <td className="py-2 pl-2">
                                            <div className="flex items-center gap-1">
                                              <Button
                                                label="Modifier"
                                                intent="ghost"
                                                size="sm"
                                                onClick={() => {
                                                  setBundleModalProduct(bundleProduct ?? null);
                                                  setEditingBundleId(qb.id);
                                                }}
                                              />
                                              <Button
                                                label="Retirer"
                                                intent="ghost"
                                                size="sm"
                                                onClick={async () => {
                                                  if (!quoteId) return;
                                                  try {
                                                    await dissociateQuoteBundle(quoteId, qb.id);
                                                    const data = await getQuote(quoteId);
                                                    setQuote(data);
                                                  } catch (e) {
                                                    setError(e instanceof Error ? e.message : "Erreur");
                                                  }
                                                }}
                                              />
                                            </div>
                                          </td>
                                        </tr>
                                      );
                                    })}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          )}
                        </div>
                  {/* Résumé des coûts des bundles (bas de section) */}
                  {quote?.bundles && quote.bundles.length > 0 && (
                    <div className="rounded-lg border border-border bg-muted/10 p-4 mt-6">
                      <h3 className="text-sm font-semibold text-text mb-3">Résumé des coûts des bundles</h3>
                      {[...(quote.bundles || [])]
                        .sort((a, b) => a.position - b.position)
                        .map((qb) => {
                          const bundleLines = (quote.lines ?? []).filter((l) => l.quote_bundle_id === qb.id);
                          const bundleTotal = bundleLines.reduce((s, l) => s + l.total_price, 0);
                          return (
                            <div key={qb.id} className="mb-4 last:mb-0">
                              <div className="font-medium text-text mb-1">{qb.name}</div>
                              <div className="overflow-x-auto">
                                <table className="w-full text-sm border-collapse">
                                  <tbody className="text-muted">
                                    {bundleLines.map((line) => (
                                      <tr key={line.id}>
                                        <td className="py-0.5 pr-2 truncate">{line.label}</td>
                                        <td className="py-0.5 pr-2 text-right tabular-nums">
                                          {line.quantity} {line.unit_kind ?? ""}
                                        </td>
                                        <td className="py-0.5 pr-2 text-right tabular-nums whitespace-nowrap">
                                          {formatCurrency(line.unit_price)}
                                        </td>
                                        <td className="py-0.5 text-right tabular-nums whitespace-nowrap">
                                          {formatCurrency(line.total_price)}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                              <div className="flex justify-end mt-1 text-sm font-medium text-text">
                                Total : {formatCurrency(bundleTotal)}
                              </div>
                            </div>
                          );
                        })}
                      <div className="flex justify-between gap-2 font-semibold text-text border-t border-border pt-3 mt-3">
                        <span>Total des bundles</span>
                        <span className="tabular-nums">
                          {formatCurrency(
                            (quote.bundles ?? []).reduce((sum, qb) => {
                              const lines = (quote.lines ?? []).filter((l) => l.quote_bundle_id === qb.id);
                              return sum + lines.reduce((s, l) => s + l.total_price, 0);
                            }, 0)
                          )}
                        </span>
                      </div>
                    </div>
                  )}
                      </>
                    );
                  })()}
                </div>
              ) : (
                <ul className="space-y-2">
                  {catalogProducts.map((product) => {
                    const lineInQuote = quote?.lines?.find((l) => l.product_id === product.id && !l.quote_bundle_id);
                    const inQuote = !!lineInQuote;
                    const adding = addingProductId === product.id;
                    const qty = quantityByProductId[product.id] ?? 1;
                    const updating = lineInQuote && lineUpdatingId === lineInQuote.id;
                    const removing = lineInQuote && lineRemovingId === lineInQuote.id;
                    return (
                      <li
                        key={product.id}
                        className="flex flex-wrap items-center justify-between gap-3 rounded-lg border border-border bg-surface px-4 py-3"
                      >
                        <div className="min-w-0 flex-1">
                          <div className="font-medium text-text">{product.name}</div>
                          <div className="mt-0.5 flex items-center gap-2 text-xs text-muted">
                            {product.unit_kind && (
                              <span>Unité : {product.unit_kind}</span>
                            )}
                            {product.code && <span>· {product.code}</span>}
                          </div>
                          {product.description_md && (
                            <p className="mt-0.5 line-clamp-2 text-sm text-muted">
                              {product.description_md.replace(/#+/g, "").trim().slice(0, 120)}
                              {product.description_md.length > 120 ? "…" : ""}
                            </p>
                          )}
                        </div>
                        <div className="flex shrink-0 items-center gap-2">
                          {inQuote && lineInQuote ? (
                            <>
                              <label className="flex items-center gap-1.5 text-sm text-muted">
                                <span>Qté</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={9999}
                                  step={1}
                                  value={lineQuantityDraft[lineInQuote.id] ?? lineInQuote.quantity}
                                  onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    if (!Number.isNaN(v)) setLineQuantityDraft((prev) => ({ ...prev, [lineInQuote.id]: Math.max(1, Math.round(v)) }));
                                  }}
                                  onBlur={() => {
                                    const draft = lineQuantityDraft[lineInQuote.id];
                                    if (draft != null && draft !== lineInQuote.quantity) handleUpdateQuoteLineQuantity(lineInQuote.id, draft);
                                    setLineQuantityDraft((prev) => {
                                      const next = { ...prev };
                                      delete next[lineInQuote.id];
                                      return next;
                                    });
                                  }}
                                  className="w-14 rounded border border-border bg-surface px-2 py-1 text-right text-sm tabular-nums"
                                  disabled={!!updating}
                                />
                                {product.unit_kind && <span className="text-xs">{product.unit_kind}</span>}
                              </label>
                              <Button
                                label={removing ? "Retrait…" : "Retirer"}
                                intent="ghost"
                                size="sm"
                                onClick={() => handleRemoveProductFromQuote(lineInQuote.id)}
                                disabled={!!lineRemovingId}
                                state={removing ? "loading" : "default"}
                              />
                            </>
                          ) : (
                            <>
                              <label className="flex items-center gap-1.5 text-sm text-muted">
                                <span>Qté</span>
                                <input
                                  type="number"
                                  min={1}
                                  max={9999}
                                  step={1}
                                  value={qty}
                                  onChange={(e) => {
                                    const v = parseInt(e.target.value, 10);
                                    if (!Number.isNaN(v)) setQuantityByProductId((prev) => ({ ...prev, [product.id]: Math.max(1, Math.round(v)) }));
                                  }}
                                  className="w-14 rounded border border-border bg-surface px-2 py-1 text-right text-sm tabular-nums"
                                />
                                {product.unit_kind && <span className="text-xs">{product.unit_kind}</span>}
                              </label>
                              <Button
                                label={adding ? "Ajout…" : "Ajouter"}
                                intent="primary"
                                size="sm"
                                onClick={() => handleAddProductToQuote(product, qty)}
                                disabled={!!addingProductId}
                              />
                            </>
                          )}
                        </div>
                      </li>
                    );
                  })}
                </ul>
              )}
            </div>
          )}

          {/* Sections sans catalogue (Bundles, Services personnalisés) : placeholder */}
          {activeSection !== "summary" && !CATALOG_SECTION_IDS.has(activeSection) && (
            <div className="rounded-md border border-dashed border-border bg-muted/20 p-8 text-center">
              <p className="text-sm text-muted">
                {activeSection === "bundles"
                  ? "Recherche catalogue bundles ; ajout d’un bundle puis composition (items autorisés)."
                  : activeSection === "parts"
                    ? "Recherche pièces/accessoires (SKU, tags) ; ajout quantité, prix, remise."
                    : activeSection === "service_fixed"
                      ? "Catalogue services à prix fixe ; quantité, date souhaitée, notes."
                      : activeSection === "service_variable"
                        ? "Services tarifés par unité (acre, ha, km) ; quantité, taux, option « estimé »."
                        : activeSection === "service_custom"
                          ? "Ligne libre : titre, description, forfait ou horaire."
                          : activeSection === "subscription"
                            ? "Plans abonnement / licence ; durée, date de début, renouvellement."
                            : activeSection === "credits"
                              ? "Quantité de crédits XLHub ; prix unitaire."
                              : "Contenu à brancher (catalogue + lignes)."}
            </p>
            <p className="mt-2 text-xs text-muted">
              L’API et les lignes de devis seront branchées au fil des sprints (Quote Builder).
            </p>
          </div>
          )}
          </div>
          )}
        </main>

        {/* Panneau droit : résumé soumission (collapsible) */}
        <aside
          className={cn(
            "flex flex-col border-l border-border bg-surface shrink-0 overflow-hidden transition-[width] duration-200",
            summaryPanelCollapsed ? "w-12" : "w-full sm:w-[380px] xl:w-[420px]"
          )}
        >
          <div className="flex h-full min-h-0 flex-col">
            <div className="flex shrink-0 items-center border-b border-border bg-muted/30">
              {summaryPanelCollapsed ? (
                <button
                  type="button"
                  onClick={() => setSummaryPanelCollapsed(false)}
                  className="flex h-12 w-12 flex-shrink-0 items-center justify-center text-muted hover:bg-surface-muted hover:text-text"
                  title="Ouvrir le résumé"
                  aria-label="Ouvrir le résumé de la soumission"
                >
                  <PanelRightOpen size={20} />
                </button>
              ) : (
                <>
                  <div className="min-w-0 flex-1 px-4 py-3">
                    <h3 className="text-sm font-semibold text-text truncate">Résumé soumission</h3>
                    {quoteId && quote && (
                      <p className="text-xs text-muted mt-0.5 truncate">
                        {quote.quote_number} · {quote.status === "draft" ? "Brouillon" : quote.status}
                      </p>
                    )}
                  </div>
                  <button
                    type="button"
                    onClick={() => setSummaryPanelCollapsed(true)}
                    className="flex h-10 w-10 flex-shrink-0 items-center justify-center text-muted hover:bg-surface-muted hover:text-text mr-1 rounded"
                    title="Réduire le panneau"
                    aria-label="Réduire le résumé"
                  >
                    <PanelRightClose size={18} />
                  </button>
                </>
              )}
            </div>
            {!summaryPanelCollapsed && (
              <div className="flex-1 min-h-0 overflow-y-auto overflow-x-hidden p-4">
              {!quoteId && (
                <p className="text-sm text-muted text-center py-6">
                  Sélectionnez un client et créez la soumission pour voir le résumé ici.
                </p>
              )}
              {quoteId && loading && !quote && (
                <div className="flex items-center justify-center gap-2 py-8 text-muted">
                  <Loader2 className="h-5 w-5 animate-spin" />
                  <span className="text-sm">Chargement…</span>
                </div>
              )}
              {quoteId && quote && (
                <div className="space-y-4">
                  {quote.lines.length === 0 ? (
                    <p className="text-sm text-muted text-center py-4">Aucune ligne. Ajoutez des produits ou des bundles.</p>
                  ) : (
                    <>
                      {quote.bundles?.length
                        ? [...(quote.bundles || [])]
                            .sort((a, b) => a.position - b.position)
                            .map((bundle) => {
                              const bundleLines = quote.lines
                                .filter((l) => l.quote_bundle_id === bundle.id)
                                .sort((a, b) => a.line_order - b.line_order);
                              if (bundleLines.length === 0) return null;
                              return (
                                <div key={bundle.id} className="rounded border border-border bg-muted/10 p-2">
                                  <div className="mb-1.5 text-xs font-medium text-text">{bundle.name}</div>
                                  <ul className="space-y-1 text-sm">
                                    {bundleLines.map((line) => (
                                      <li key={line.id} className="flex justify-between gap-2 text-muted">
                                        <span className="min-w-0 truncate">{line.label}</span>
                                        <span className="shrink-0 whitespace-nowrap">
                                          {line.quantity} {line.unit_kind ? `(${line.unit_kind})` : ""} × {formatCurrency(line.unit_price)} = {formatCurrency(line.total_price)}
                                        </span>
                                      </li>
                                    ))}
                                  </ul>
                                </div>
                              );
                            })
                        : null}
                      {quote.lines
                        .filter((l) => !l.quote_bundle_id)
                        .sort((a, b) => a.line_order - b.line_order)
                        .map((line) => (
                          <div
                            key={line.id}
                            className="flex justify-between gap-2 rounded border border-border bg-muted/5 px-3 py-2 text-sm"
                          >
                            <span className="min-w-0 truncate text-text">{line.label}</span>
                            <span className="shrink-0 text-muted whitespace-nowrap">
                              {line.quantity} {line.unit_kind ? `(${line.unit_kind})` : ""} × {formatCurrency(line.unit_price)} = {formatCurrency(line.total_price)}
                            </span>
                          </div>
                        ))}
                      <div className="flex flex-col gap-1 border-t border-border pt-3 text-sm">
                        <div className="flex justify-between gap-2">
                          <span className="text-muted">Sous-total</span>
                          <span>{formatCurrency(quote.subtotal)}</span>
                        </div>
                        <div className="flex justify-between gap-2">
                          <span className="text-muted">Taxes</span>
                          <span>{formatCurrency(quote.tax_total ?? 0)}</span>
                        </div>
                        <div className="flex justify-between gap-2 font-medium text-text">
                          <span>Total</span>
                          <span>{formatCurrency(quote.total)}</span>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              )}
              </div>
            )}
          </div>
        </aside>
      </div>

      <ConfigureBundleModal
        open={!!bundleModalProduct}
        bundle={bundleModalProduct}
        quoteId={quoteId}
        editBundle={
          editingBundleId && quote?.bundles
            ? quote.bundles.find((b) => b.id === editingBundleId) ?? null
            : null
        }
        existingLines={
          editingBundleId && quote?.lines
            ? quote.lines.filter((l) => l.quote_bundle_id === editingBundleId)
            : []
        }
        onClose={() => {
          setBundleModalProduct(null);
          setEditingBundleId(null);
        }}
        onSaved={async () => {
          if (quoteId) {
            try {
              const data = await getQuote(quoteId);
              setQuote(data);
            } catch (_e) {
              setError("Erreur rechargement soumission");
            }
          }
        }}
      />

      <SendQuoteModal
        open={sendModalOpen}
        onClose={() => setSendModalOpen(false)}
        quoteNumber={quote?.quote_number ?? ""}
        clientId={quote?.client_id ?? null}
        primaryContactEmail={primaryContactEmail}
        onSend={async (recipients) => {
          if (!quoteId || !quote || recipients.to.length === 0) return;
          const ctx: Record<string, unknown> = {
            ...(quote.pricing_context as Record<string, unknown>),
            client_email: recipients.to[0],
            email_cc: recipients.cc?.length ? recipients.cc : undefined,
            email_bcc: recipients.bcc?.length ? recipients.bcc : undefined,
          };
          await updateQuote(quoteId, { pricing_context: ctx });
          const data = await getQuote(quoteId);
          setQuote(data);
          await sendQuote(quoteId);
          const updated = await getQuote(quoteId);
          setQuote(updated);
        }}
      />
    </AppShell>
  );
}
